import { AwardsSubTabs } from '@admin/engagement/awards/useAwardsSubTabs'
import { CelebrationsTabs } from '@admin/engagement/celebrations/useCelebrationsTabs'
import { IncentiveTabs } from '@admin/engagement/incentives/incentive/useIncentiveTabs'
import { FtpTabs } from '@admin/integrations/ftp/useFtpTabs'
import { TranslationsTabs } from '@admin/platformSetup/languageAndTime/translations/useTranslationTabs'
import { CustomRewardTabs } from '@admin/pointsAndRewards/rewards/custom/reward/useCustomRewardTabs'
import { EmailSharingSubTabs } from '@admin/reports/reportSharing/useEmailSharingSubTabs'
import { DelegationTabs } from '@admin/userManagement/delegation/useDelegationTabs'
import { HrisIntegrationTabs } from '@admin/userManagement/hrisIntegration/useHrisIntegrationTabs'
import { LanguagesEnum, ReportType, RewardProviderType } from '@kudos/http-client'
import { AnalyticsTabs } from '@tenant/admin/adminTools/burnoutInsights/useAnalyticsTabs'
import { ContentTabs } from '@tenant/admin/adminTools/flaggedContent/useContentTabs'
import { PointsTabs } from '@tenant/admin/pointsAndRewards/points/usePointsTabs'
import { ReportSharingTabs } from '@tenant/admin/reports/reportSharing/useReportSharingTabs'
import { MemberTabs } from '@tenant/admin/userManagement/members/member/useMemberTabs'
import { MembersTabs } from '@tenant/admin/userManagement/members/useMembersTabs'

type PointRulesGroup = 'peer' | 'celebration' | 'bank'
export type PointAllocationAnchors = 'userPoints' | 'celebrationPoints' | 'pointsBank'
export default class AdminPaths {
  // Members
  public static members(tab: MembersTabs | ':tab?') {
    return `/manage/${tab}`
  }
  public static membersBulkImportCsv() {
    return '/manage/users/import/csv'
  }
  public static member(memberId: string, tab: MemberTabs | ':tab?') {
    return `/user/${memberId}/${tab}`
  }
  public static customize() {
    return '/customize'
  }
  // Staff
  public static staffMember(staffId: string) {
    return `/staffing/${staffId}`
  }

  // Security
  public static security() {
    return '/tenant/security'
  }

  // Check-ins
  public static checkInManage() {
    return '/check-ins/manage'
  }
  public static editAudience(categoryId: string) {
    return `/check-ins/category/${categoryId}/audience`
  }

  // Analytics & Insights
  public static analytics(tab?: AnalyticsTabs | ':tab?') {
    return `/analytics/${tab ?? ''}`
  }
  public static insightDetails(insightId: string) {
    return `/analytics/insight/${insightId}`
  }

  // Teams
  public static createTeam(isAutomated: boolean = false) {
    return `/team/create${isAutomated ? '?automated=true' : ''}`
  }
  public static viewTeam(teamId: string) {
    return `/team/${teamId}`
  }
  public static configureTeamAutomation(teamId: string) {
    return `/team/${teamId}/automation`
  }
  public static batchAddToTeams(batchId: string) {
    return `/team/batch-add/${batchId}`
  }
  public static editTeam(teamId: string) {
    return `/team/${teamId}/edit`
  }
  public static configureQuickTeams() {
    return '/team/quick-teams/configure'
  }

  // Values and qualities
  public static values() {
    return '/values'
  }
  public static configureValues() {
    return '/values/configure'
  }
  public static editValue(valueId: string) {
    return `/values/${valueId}/edit`
  }

  // Content management
  public static flaggedContent(tab?: ContentTabs | ':tab?') {
    return `/content/flagged/${tab ?? ''}`
  }

  // Points & budget management
  public static points(tab?: PointsTabs | ':tab?', anchor?: PointAllocationAnchors) {
    let tabUrl = `/points/${tab ?? ''}`
    if (!!anchor) {
      tabUrl = `${tabUrl}#${anchor}`
    }
    return tabUrl
  }
  public static individualPointsReporting(memberId: string) {
    return `/points/reporting/${memberId}`
  }
  public static pointsRules(type: PointRulesGroup | ':type?') {
    return `/points/${type}`
  }

  // Notifications
  public static notifications() {
    return '/notifications'
  }

  // Rewards
  public static rewards(tab: 'setup') {
    return `/rewards/${tab}`
  }
  public static editRewardProvider(rewardProviderId: string) {
    return `/rewards/${rewardProviderId}/edit`
  }

  public static addTangoRewardFunds(rewardProviderId: string) {
    return `${this.rewardProvider(RewardProviderType.TANGO, rewardProviderId)}/add-funds`
  }

  public static configureRewardProvider(
    type: RewardProviderType,
    rewardProviderId?: string | ':rewardProviderId?',
  ) {
    const baseUrl = '/rewards/configure'
    if (type === RewardProviderType.TANGO) {
      return rewardProviderId ? `${baseUrl}/${rewardProviderId}` : baseUrl
    }
    return rewardProviderId ? `${baseUrl}/${type}/${rewardProviderId}` : `${baseUrl}/${type}`
  }

  public static rewardProvider(type: RewardProviderType, rewardProviderId: string) {
    const baseUrl = '/rewards/store'
    return type === RewardProviderType.TANGO
      ? `${baseUrl}/${rewardProviderId}`
      : `${baseUrl}/${type}/${rewardProviderId}`
  }

  public static configureCustomReward(
    rewardProviderId: string | ':rewardProviderId?',
    customRewardId?: string | ':customRewardId?',
  ) {
    const baseUrl = this.rewardProvider(RewardProviderType.CUSTOM, rewardProviderId)
    return customRewardId
      ? `${baseUrl}/reward/configure/${customRewardId}`
      : `${baseUrl}/reward/configure`
  }

  public static duplicateCustomReward(
    rewardProviderId: string | ':rewardProviderId?',
    customRewardId: string | ':customRewardId?',
  ) {
    const baseUrl = this.rewardProvider(RewardProviderType.CUSTOM, rewardProviderId)
    return `${baseUrl}/reward/duplicate/${customRewardId}`
  }

  public static customReward(
    rewardProviderId: string | ':rewardProviderId?',
    customRewardId: string | ':customRewardId?',
    tab: CustomRewardTabs | ':tab?',
  ) {
    const baseUrl = this.rewardProvider(RewardProviderType.CUSTOM, rewardProviderId)
    return `${baseUrl}/reward/${customRewardId}/${tab}`
  }

  public static bulkCompleteCustomOrders(
    rewardProviderId: string | ':rewardProviderId',
    customRewardId: string | ':customRewardId',
    batchId: string | ':batchId',
  ) {
    const baseUrl = this.rewardProvider(RewardProviderType.CUSTOM, rewardProviderId)
    return `${baseUrl}/custom/reward/${customRewardId}/complete-batch/${batchId}`
  }

  // Logs
  public static logs() {
    return '/logs'
  }

  // Celebrations
  public static configureCelebrations(tab: CelebrationsTabs | ':tab?') {
    return `/celebrations/configure/${tab}`
  }

  // Awards
  public static awards(subTab?: AwardsSubTabs | ':subTab?') {
    return `/awards/list/${subTab}`
  }

  public static viewAwardType(awardTypeId: string | ':awardTypeId') {
    return `/awards/award/${awardTypeId}`
  }
  public static viewNominationAwardType(awardTypeId: string | ':awardTypeId') {
    return `/awards/nomination/${awardTypeId}`
  }
  public static viewNominee(awardTypeId: string | ':awardTypeId', nomineeId: string | 'nomineeId') {
    return `/awards/nomination/${awardTypeId}/nominee/${nomineeId}`
  }
  public static configureAwardType(awardTypeId?: string | ':awardTypeId?') {
    const baseUrl = '/awards/configure'
    return awardTypeId ? `${baseUrl}/${awardTypeId}` : baseUrl
  }
  public static configureNomination(nominationId?: string | ':nominationId?') {
    const baseUrl = '/nominations/configure'
    return nominationId ? `${baseUrl}/${nominationId}` : baseUrl
  }

  public static bulkSendAward(awardTypeId?: string | ':awardTypeId?') {
    return `/awards/award/bulk/${awardTypeId}`
  }

  // Incentives
  public static manageIncentives() {
    return '/incentives/manage'
  }
  public static manageIncentive(incentiveId: string | ':id?', tab: IncentiveTabs | ':tab?') {
    return `/incentives/manage/${incentiveId}/${tab}`
  }
  public static configureIncentive(incentiveId?: string | ':id?') {
    const baseUrl = '/incentives/configure'
    return incentiveId ? `${baseUrl}/${incentiveId}` : baseUrl
  }
  public static duplicateIncentive(incentiveId: string) {
    return `/incentives/duplicate/${incentiveId}`
  }

  // Reports
  public static allReports() {
    return '/reports/all'
  }
  public static reports(
    type: ReportType | ':type?',
    action?: 'share',
    tab?: ReportSharingTabs | ':tab?',
    subTab?: EmailSharingSubTabs | ':subTab?',
  ) {
    const baseUrl = `/reports/${type}`
    if (!action) return baseUrl
    if (!tab) return `${baseUrl}/${action}`
    if (!subTab) return `${baseUrl}/${action}/${tab}`
    return `${baseUrl}/${action}/${tab}/${subTab}`
  }

  // Announcements
  public static announcements(announcementId?: string | ':id?') {
    const baseUrl = '/announcements'
    return announcementId ? `${baseUrl}/${announcementId}` : baseUrl
  }
  public static configureAnnouncement(announcementId?: string | ':id?') {
    const baseUrl = '/announcements/configure'
    return announcementId ? `${baseUrl}/${announcementId}` : baseUrl
  }

  // Language and time
  public static localization() {
    return '/localization'
  }
  public static translations(
    languageId: LanguagesEnum | ':languageId?',
    tab?: TranslationsTabs | ':tab?',
  ) {
    let baseUrl = `/localization/translations/${languageId}`
    if (tab) {
      baseUrl = `${baseUrl}/${tab}`
    }
    return baseUrl
  }

  // Dashboard
  public static dashboard() {
    return '/dashboard'
  }

  // Integrations
  public static integrations() {
    return '/integrations'
  }
  public static hrisIntegrations(tab: HrisIntegrationTabs | ':tab') {
    return `/staffing/import/integrations/${tab}`
  }
  public static ssoIntegrations() {
    return '/integrations/sso'
  }
  public static batchAddStaff(batchId: string) {
    return `/staffing/batch-add/${batchId}`
  }
  public static kudosTvIntegrations() {
    return '/integrations/kudos-tvs'
  }
  public static ftpIntegrations(tab: FtpTabs | ':tab?') {
    return `/integrations/ftp/${tab}`
  }
  public static msteamsIntegrations() {
    return '/integrations/msteams'
  }

  // FTP
  public static configureFtp(connectionId?: string | ':connectionId?') {
    return `/integrations/ftp/configure/${connectionId ?? ''}`
  }
  public static viewFtpSummary(connectionId: string) {
    return `ftp/${connectionId}/imports`
  }

  // SSO
  public static createSso() {
    return '/integrations/sso/create'
  }
  public static ssoProvider(ssoProviderId: string) {
    return `/integrations/sso/${ssoProviderId}`
  }

  // Recognition
  public static manageRecognition() {
    return '/recognition/manage'
  }

  // Delegation
  public static delegation(tab?: DelegationTabs | ':tab?') {
    const baseUrl = '/delegation'
    if (!tab) return baseUrl
    return `${baseUrl}/${tab}`
  }
  public static createDelegates() {
    return '/delegation/create'
  }
  public static editDelegate(delegationId: string) {
    return `/delegation/${delegationId}/edit`
  }
  public static delegateAwards() {
    return '/delegation/awards'
  }
  public static delegateBulkSendAward(awardTypeId?: string | ':awardTypeId?') {
    return `/delegation/awards/bulk/${awardTypeId}`
  }
}
